import { classifyDrmStatus, PlaybackError } from '../errors';
import {
  mediaHostSince,
  readFrameStats,
  type AudioTrackInfo,
  type PlaybackStats,
  type QualityLevel,
  type TextTrackInfo,
} from '../stats';
import { type EngineDeps, type PlaybackEngine } from './types';

// Non-standard `video.audioTracks` (Safari) — minimal structural shape.
interface NativeAudioTrack {
  enabled: boolean;
  label: string;
  language: string;
}
interface NativeAudioTrackList {
  readonly length: number;
  [index: number]: NativeAudioTrack;
}

/**
 * Pull the 32-char hex content IV out of Safari's FairPlay initData.
 *
 * WebKit hands `encrypted` the playlist key URI verbatim — `skd://<kid>:<iv>`
 * as UTF-8 — which is also Axinom's documented asset ID. Returns undefined
 * for any other shape so a malformed URI sends no header at all rather than a
 * stale or wrong one.
 */
function extractSkdIV(initData: ArrayBuffer): string | undefined {
  try {
    const uri = new TextDecoder('utf-8').decode(new Uint8Array(initData));
    return /^skd:\/\/[0-9a-fA-F-]{36}:([0-9a-fA-F]{32})$/.exec(uri)?.[1];
  } catch {
    return undefined;
  }
}

/** True when the browser plays HLS natively (Safari / iOS). */
export function nativeHlsSupported(video?: HTMLVideoElement): boolean {
  const el =
    video ?? (typeof document !== 'undefined' ? document.createElement('video') : undefined);
  return Boolean(el?.canPlayType('application/vnd.apple.mpegurl'));
}

/**
 * True when the media stack is WebKit's — the only one that decrypts FairPlay
 * through native HLS.
 *
 * `nativeHlsSupported` cannot answer this. Chromium returns `"maybe"` for
 * `canPlayType('application/vnd.apple.mpegurl')` (measured 2026-08-07) while
 * being unable to play HLS natively at all, so gating the FairPlay engine on
 * it alone routes Chrome into the native engine and fails every playback with
 * an opaque 'native HLS playback error'.
 *
 * Both signals are present on Safari and on iOS third-party browsers (all
 * WebKit), and absent on Chromium — which reports `vendor: "Google Inc."` and
 * exposes neither legacy FairPlay entry point. OR rather than AND so a future
 * WebKit that drops one of them still reaches the engine that works.
 */
export function webkitMediaStack(): boolean {
  if (typeof window === 'undefined') return false;
  const legacyFairPlay =
    'WebKitMediaKeys' in window ||
    (typeof HTMLMediaElement !== 'undefined' && 'webkitSetMediaKeys' in HTMLMediaElement.prototype);
  const appleVendor = typeof navigator !== 'undefined' && /Apple/i.test(navigator.vendor ?? '');
  return legacyFairPlay || appleVendor;
}

/**
 * Native-HLS engine — plays via `video.src`, and drives FairPlay EME itself.
 *
 * This is the FairPlay engine on Apple platforms. The previous note here said
 * FairPlay was "implemented on the hls.js engine, which 'auto' prefers
 * everywhere hls.js is supported (including modern Safari via
 * ManagedMediaSource)". That was wrong, and it cost a lot of debugging: the
 * hls.js/MSE route reaches a 'usable' key and still renders nothing, because
 * WebKit binds FairPlay keys through the fragment `sinf` box rather than a
 * synthesized skd asset ID. Measured on macOS Safari 26.5 against live DRM
 * matches (2026-08-06): hls.js+MSE 1 pass / 6 attempts, this engine 4 / 4 at
 * 1280px (Widevine on Chrome 3/3 as the control).
 *
 * Widevine is not available here — that stays on the hls.js engine.
 */
export class NativeHlsEngine implements PlaybackEngine {
  readonly name = 'native' as const;
  private readonly deps: EngineDeps;
  private onLoaded?: () => void;
  private onError?: () => void;
  private onEncrypted?: (ev: Event) => void;
  private fairplayLicenseUrl: string | undefined;
  /** Kept so teardown can close the CDM session. */
  private keySession: MediaKeySession | null = null;
  /** Set synchronously on the first `encrypted` event; see armFairPlay. */
  private fairplayArmed = false;
  /** Content IV (32-char hex) parsed from Safari's skd:// initData. */
  private fairplayKeyIV: string | undefined;
  /** False while a deferLoad engine waits for startLoad(); see load(). */
  private loadStarted: boolean;
  /** True once load() rewrote the element's preload; destroy() restores it. */
  private preloadTouched = false;
  /** The preload attribute before load() rewrote it (null = not set). */
  private savedPreload: string | null = null;
  /** CDN host, recovered from Resource Timing once and kept; see getStats. */
  private cdnHost = '';
  /** Resource-timeline mark taken at construction, so a stream played EARLIER
   *  on this page cannot donate its host to this session. */
  private readonly builtAt: number;

  constructor(deps: EngineDeps) {
    this.deps = deps;
    this.loadStarted = deps.deferLoad !== true;
    this.builtAt = typeof performance !== 'undefined' ? performance.now() : 0;
  }

  async load(): Promise<void> {
    const { video, descriptor, emit } = this.deps;

    // deferLoad (autoplay-off pre-play): preload 'none' keeps the native
    // pipeline from fetching the manifest, media, or FairPlay keys until the
    // viewer presses play. 'ready' (loadedmetadata) then arrives after the
    // first play() starts fetching — the managed player does not wait on it.
    if (!this.loadStarted) {
      this.preloadTouched = true;
      this.savedPreload = video.getAttribute('preload');
      video.preload = 'none';
    }

    if (descriptor.drmEnabled) {
      if (!descriptor.drm?.fairplay) {
        // DRM required but no FairPlay material (e.g. a Widevine-only stream): the
        // native engine cannot decrypt it. Fail fast with a clear, typed error
        // instead of an opaque 'native HLS playback error' downstream.
        emit({
          type: 'error',
          error: new PlaybackError(
            'DRM_CLIENT',
            0,
            'native HLS engine cannot satisfy this DRM (Widevine requires the hls.js engine); use engine "auto"/"hls" on a Widevine-capable browser',
          ),
          fatal: true,
        });
        return;
      }
      this.armFairPlay();
    }

    // deferLoad: the managed layer owns the pre-play 'ready' for this engine
    // (emitted after analytics/session wiring, a macrotask after commit — an
    // emit here would fire inside load(), before the analytics session exists
    // and before the host can hold listeners). loadedmetadata can't fire
    // until play() starts fetching under preload="none", so the engine-side
    // emit is suppressed entirely for a deferred instance — it would only
    // duplicate the managed one after startLoad() + play().
    const deferred = !this.loadStarted;
    this.onLoaded = deferred ? undefined : () => emit({ type: 'ready' });
    this.onError = () =>
      emit({
        type: 'error',
        error: new PlaybackError('INTERNAL', 0, 'native HLS playback error'),
        fatal: true,
      });
    if (this.onLoaded) video.addEventListener('loadedmetadata', this.onLoaded, { once: true });
    video.addEventListener('error', this.onError);
    video.src = withCmcdSid(descriptor.manifestUrl, descriptor.analytics?.sid);
    video.load();
  }

  startLoad(): void {
    if (this.loadStarted) return;
    this.loadStarted = true;
    // Putting the host's own preload back is enough: the play() that follows
    // drives the fetch regardless of the attribute's value, and forcing
    // 'auto' would override a host-set "metadata" for the rest of the
    // session. An explicit video.load() here would abort that play()'s
    // pending promise (the media load algorithm rejects it with AbortError).
    this.restorePreload();
  }

  /** Undo the deferLoad preload rewrite so the element reads as the host set
   *  it. Idempotent; a no-op when load() never rewrote it. */
  private restorePreload(): void {
    if (!this.preloadTouched) return;
    this.preloadTouched = false;
    const { video } = this.deps;
    if (this.savedPreload !== null) video.setAttribute('preload', this.savedPreload);
    else video.removeAttribute('preload');
  }

  /**
   * Drive FairPlay EME on the native HLS pipeline.
   *
   * This is how FairPlay is meant to be deployed on Apple platforms, and it
   * is the path that actually decrypts. Safari raises `encrypted` with
   * initDataType 'skd' and the playlist key URI as initData — the full
   * `skd://<kid>:<iv>` string, UTF-8, 75 bytes — which is exactly Axinom's
   * documented asset ID. We never synthesize or reshape it.
   *
   * The hls.js/MSE route cannot do this: WebKit binds a FairPlay key to
   * media through the fragment `sinf` box, so a session built from a
   * synthesized skd asset ID is never associated with the stream (key
   * reports 'usable', element sits at waitingforkey, black), and driving it
   * from the sinf instead loses the IV that only the URI carries. Measured
   * on macOS Safari 26.5 against live DRM matches (2026-08-06): hls.js+MSE
   * 1 pass / 6, this path 4 passes / 4 at 1280px, with Widevine on Chrome
   * 3/3 as the control.
   *
   * Requires drm-proxy to put the content IV on every inline key of the
   * entitlement message (`content_keys_source.inline[].iv`) — Axinom rejects
   * a FairPlay request without one unless the asset ID supplies it.
   */
  private armFairPlay(): void {
    const { video, descriptor, credential, deviceId, userId, emit } = this.deps;
    const fp = descriptor.drm?.fairplay;
    if (!fp) return;
    this.fairplayLicenseUrl = fp.licenseUrl;

    // Only a server auth/entitlement refusal is UNAUTHORIZED/FORBIDDEN;
    // certificate/CDM/transport failures are DRM_CLIENT so callers can tell
    // "not allowed" from "broke on this device".
    const fail = (message: string, status: number, fatal: boolean) =>
      emit({
        type: 'error',
        error: new PlaybackError(classifyDrmStatus(status), status, message),
        fatal,
      });

    this.onEncrypted = (event: Event) => {
      const ev = event as MediaEncryptedEvent;
      // Safari raises `encrypted` several times in quick succession (once per
      // track / init segment). The guard must flip BEFORE the first await or
      // every event enters the async block and races to build its own session.
      if (!ev.initData || this.fairplayArmed) return;
      this.fairplayArmed = true;
      // Safari's initData IS the playlist key URI: skd://<kid>:<iv>, UTF-8.
      // Axinom needs that IV on every inline key of the entitlement message,
      // and drm-proxy has no other source for it, so pass it along.
      this.fairplayKeyIV = extractSkdIV(ev.initData);
      void (async () => {
        try {
          const access = await navigator.requestMediaKeySystemAccess('com.apple.fps', [
            {
              initDataTypes: [ev.initDataType],
              videoCapabilities: [{ contentType: 'application/vnd.apple.mpegurl', robustness: '' }],
              distinctiveIdentifier: 'not-allowed',
              persistentState: 'not-allowed',
              sessionTypes: ['temporary'],
            },
          ]);
          const mediaKeys = await access.createMediaKeys();
          // drm-proxy requires the api key on the certificate GET.
          const certRes = await fetch(fp.certificateUrl, {
            headers: { 'x-api-key': credential.apiKey },
          });
          if (!certRes.ok) {
            // Carry the real status so a certificate 401/403 keeps the server
            // taxonomy instead of collapsing to DRM_CLIENT/0 in the catch below.
            this.fairplayArmed = false; // allow a retry on the next `encrypted`
            fail(`FairPlay certificate request failed: ${certRes.status}`, certRes.status, true);
            return;
          }
          await mediaKeys.setServerCertificate(await certRes.arrayBuffer());
          await video.setMediaKeys(mediaKeys);

          const session = mediaKeys.createSession();
          this.keySession = session;
          session.addEventListener('message', (msgEvent) => {
            void (async () => {
              try {
                // Read the URL fresh: setLicenseUrls may have refreshed the
                // signed URL since the session was created (10-min TTL).
                const res = await fetch(this.fairplayLicenseUrl ?? fp.licenseUrl, {
                  method: 'POST',
                  body: msgEvent.message,
                  headers: {
                    'x-api-key': credential.apiKey,
                    'X-Match-Urn': descriptor.matchUrn,
                    'X-Device-Id': deviceId,
                    ...(userId ? { 'X-User-Id': userId } : {}),
                    ...(this.fairplayKeyIV ? { 'X-Key-Iv': this.fairplayKeyIV } : {}),
                  },
                });
                if (!res.ok) {
                  fail(`FairPlay license request failed: ${res.status}`, res.status, true);
                  return;
                }
                await session.update(new Uint8Array(await res.arrayBuffer()));
              } catch (e) {
                fail(`FairPlay license request failed: ${String(e)}`, 0, true);
              }
            })();
          });
          await session.generateRequest(ev.initDataType, ev.initData as BufferSource);
        } catch (e) {
          this.fairplayArmed = false; // allow a retry on the next `encrypted`
          fail(`FairPlay key session failed: ${String(e)}`, 0, true);
        }
      })();
    };
    video.addEventListener('encrypted', this.onEncrypted);
  }

  setLicenseUrls(urls: { widevine?: string; fairplay?: string }): void {
    // Keep the refreshed signed FairPlay URL for the next license request;
    // Widevine is not reachable on this engine.
    if (urls.fairplay) this.fairplayLicenseUrl = urls.fairplay;
  }

  // Native HLS handles ABR internally and does not expose a rendition ladder.
  getQualityLevels(): QualityLevel[] {
    return [];
  }
  getCurrentQuality(): number {
    return -1;
  }
  setQuality(_index: number): void {}
  setMaxBitrate(_bitrate: number | null): void {}

  getAudioTracks(): AudioTrackInfo[] {
    const list = (this.deps.video as HTMLVideoElement & { audioTracks?: NativeAudioTrackList })
      .audioTracks;
    if (!list) return [];
    return Array.from({ length: list.length }, (_v, id) => {
      const t = list[id];
      return {
        id,
        name: t.label || t.language || `Audio ${id + 1}`,
        lang: t.language || undefined,
        default: t.enabled,
      };
    });
  }
  setAudioTrack(id: number): void {
    const list = (this.deps.video as HTMLVideoElement & { audioTracks?: NativeAudioTrackList })
      .audioTracks;
    if (!list) return;
    for (let i = 0; i < list.length; i += 1) list[i].enabled = i === id;
  }

  getTextTracks(): TextTrackInfo[] {
    const list = this.deps.video.textTracks;
    return Array.from({ length: list.length }, (_v, id) => {
      const t = list[id];
      return { id, name: t.label || t.language || `Text ${id + 1}`, lang: t.language || undefined };
    });
  }
  setTextTrack(id: number): void {
    const list = this.deps.video.textTracks;
    for (let i = 0; i < list.length; i += 1) list[i].mode = i === id ? 'showing' : 'disabled';
  }

  seekToLive(): void {
    const v = this.deps.video;
    if (v.seekable.length > 0) v.currentTime = v.seekable.end(v.seekable.length - 1);
  }

  getStats(): PlaybackStats {
    const v = this.deps.video;
    const frames = readFrameStats(v);
    const stats: PlaybackStats = {
      droppedFrames: frames.dropped,
      decodedFrames: frames.totalFrames,
    };
    // Request timing, ABR direction and codecs stay invisible to this engine:
    // WebKit fetches the media itself and reports no per-segment network
    // detail to script. That is plan gate G8, not a gap to fill here.
    //
    // The CDN host is the exception, and it is worth the exception: without
    // it every Safari and iOS session lands in the empty cohort of a per-CDN
    // breakdown, which on a Safari-heavy audience is most of the traffic.
    // Resource Timing still carries the media load even though the transfer
    // detail is gone — see mediaHostSince, which documents what WebKit does
    // and does not expose, and why this names the PLAYLIST's host.
    //
    // Cached on first sight: the host cannot change for this engine (there is
    // no failover to observe), the entry can be evicted once the resource
    // buffer fills, and re-scanning the timeline on every heartbeat for a
    // value that never moves is waste.
    if (!this.cdnHost) this.cdnHost = mediaHostSince(this.builtAt);
    if (this.cdnHost) stats.cdnHost = this.cdnHost;
    if (this.deps.descriptor.drmEnabled) stats.keySystem = 'fairplay';
    if (v.duration === Infinity) {
      stats.isLive = true;
      if (v.seekable.length > 0) {
        stats.atLiveEdge = v.currentTime >= v.seekable.end(v.seekable.length - 1) - 2;
      }
    }
    return stats;
  }

  stop(): void {
    // Native HLS: pause to halt fetching; keep src so the last frame stays up.
    try {
      this.deps.video.pause();
    } catch {
      // ignore
    }
  }

  destroy(): void {
    const { video } = this.deps;
    if (this.onLoaded) video.removeEventListener('loadedmetadata', this.onLoaded);
    if (this.onError) video.removeEventListener('error', this.onError);
    if (this.onEncrypted) video.removeEventListener('encrypted', this.onEncrypted);
    // Release the CDM session; a stale one would keep the key alive past
    // teardown and block a fresh arm on the next load().
    void this.keySession?.close().catch(() => {});
    this.keySession = null;
    this.fairplayArmed = false;
    this.fairplayKeyIV = undefined;
    try {
      video.pause();
    } catch {
      // ignore
    }
    video.removeAttribute('src');
    this.restorePreload(); // a still-deferred engine dies with 'none' applied
    try {
      video.load();
    } catch {
      // ignore
    }
  }
}

/**
 * Append the CMCD session id to the manifest URL (CTA-5004 query mode).
 * The native pipeline fetches segments itself with no request hook, so the
 * sid can only ride the manifest request — the known partial-attribution
 * cohort of plan gate G8. No-op without a sid.
 */
function withCmcdSid(manifestUrl: string, sid: string | undefined): string {
  if (!sid) return manifestUrl;
  const sep = manifestUrl.includes('?') ? '&' : '?';
  return `${manifestUrl}${sep}CMCD=${encodeURIComponent(`sid="${sid}"`)}`;
}
